import * as React from "react";
import LBox from "../LBox";
import LArtwork, { getDeterministicPlaceholder } from "../LArtwork";
import { ExclusiveRibbon } from "@libs/shared-ui/native";
import { theme } from "@theme";

type ArtProps = {
  blurhash?: string | null | (string | undefined | null)[];
  id?: string;
  imageUrl?: string | null | (string | undefined | null)[];
  imageSize?: "small" | "medium" | "large" | "xLarge";
  size: number;
  style?: object;
  type: "artist" | "playlist" | "album";
  borderRadius?: keyof typeof theme.borderRadii;
  hasPrivateSong?: boolean;
  privateContentIconSize?: number;
  privateContentIconPadding?: keyof typeof theme.spacing;
  privateSongIds?: string[];
};

// Exclusive content is marked with the VIP corner ribbon (LIS-602); the old
// green stroke + padlock badge treatment is gone. The compact ribbon scales
// with the artwork, the featured one is for hero-sized art.
const ExclusiveArtworkFrame = ({
  size,
  borderRadius,
  children,
}: {
  size: number;
  borderRadius?: keyof typeof theme.borderRadii;
  children: React.ReactNode;
}) => (
  <LBox
    width={size}
    height={size}
    borderRadius={borderRadius}
    overflow="hidden"
  >
    {children}
    <ExclusiveRibbon
      containerSize={size}
      variant={size >= 100 ? "featured" : "compact"}
    />
  </LBox>
);

const ArtPlaylist = ({
  blurhash,
  imageUrl,
  imageSize = "large",
  size,
  style,
  hasPrivateSong,
  id,
  borderRadius,
}: ArtProps) => {
  return (
    <LBox
      style={style}
      alignItems="center"
      justifyContent="center"
      height={size}
    >
      {hasPrivateSong ? (
        <ExclusiveArtworkFrame size={size} borderRadius={borderRadius}>
          <LArtwork
            blurhash={blurhash}
            borderRadius={borderRadius}
            imageSize={imageSize}
            size={size}
            url={imageUrl}
          />
        </ExclusiveArtworkFrame>
      ) : (
        <LArtwork
          blurhash={blurhash}
          borderRadius={borderRadius}
          imageSize={imageSize}
          size={size}
          url={imageUrl}
          placeholderImage={getDeterministicPlaceholder(id)}
        />
      )}
    </LBox>
  );
};

const ArtAlbum = ({
  blurhash,
  imageUrl,
  imageSize = "large",
  size,
  style,
  hasPrivateSong,
  borderRadius,
}: ArtProps) => {
  return (
    <LBox
      style={style}
      alignItems="center"
      justifyContent="center"
      height={size}
    >
      {hasPrivateSong ? (
        <ExclusiveArtworkFrame size={size} borderRadius={borderRadius}>
          <LArtwork
            blurhash={blurhash}
            borderRadius={borderRadius}
            imageSize={imageSize}
            size={size}
            url={imageUrl}
          />
        </ExclusiveArtworkFrame>
      ) : (
        <LArtwork
          blurhash={blurhash}
          borderRadius={borderRadius}
          imageSize={imageSize}
          size={size}
          url={imageUrl}
        />
      )}
    </LBox>
  );
};

const ArtArtist = ({
  blurhash,
  imageUrl,
  imageSize = "large",
  size,
  style,
  hasPrivateSong,
  borderRadius,
}: ArtProps) => {
  return (
    <LBox
      style={style}
      alignItems="center"
      justifyContent="center"
      height={size}
    >
      {hasPrivateSong ? (
        <ExclusiveArtworkFrame size={size} borderRadius={borderRadius}>
          <LArtwork
            blurhash={blurhash}
            borderRadius={borderRadius}
            imageSize={imageSize}
            size={size}
            url={imageUrl}
          />
        </ExclusiveArtworkFrame>
      ) : (
        <LArtwork
          blurhash={blurhash}
          borderRadius={borderRadius}
          imageSize={imageSize}
          size={size}
          url={imageUrl}
        />
      )}
    </LBox>
  );
};

const ArtFn = (props: ArtProps) => {
  if (props.type === "playlist") {
    return <ArtPlaylist {...props} />;
  }
  if (props.type === "album") {
    return <ArtAlbum {...props} />;
  }
  if (props.type === "artist") {
    return <ArtArtist {...props} />;
  }
  return null;
};

export const Art = React.memo(ArtFn);
